import React, { useCallback, useEffect, useState } from 'react';
import c from 'classnames';
import { useDispatch, useSelector } from 'react-redux';
import { Helmet } from 'react-helmet';
import { useHistory } from 'react-router';
import { useTranslation } from 'react-i18next';

import type { ImmutableRootState } from '@uncinc/react-drupal-core/types';

import { Container } from '@uncinc/uncinc-react-kitchen-sink';

import { SearchStats } from '@uncinc/react-searchkit';

import { SearchAllContent } from '../../components';
import { DEFAULT_LANGUAGE, SEARCHKIT_INDEX_ALL_CONTENT } from '../../config/constants';
import { clearPageInformation, setPageInformation, setPageViewSent } from '../../store/actions/UI';

import './style.scss';

const SearchkitSearch: React.FC = () => {
  const PAGE_TITLE = 'Zoeken | VeiligheidNL';
  const classNames = c('Searchkit');
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const history = useHistory();
  const [visible, setVisible] = useState(false);

  const initialAutocompleteQuery = useSelector<ImmutableRootState<string>, string>((state) => (
    state.getIn(['router', 'location', 'query', encodeURIComponent(`${SEARCHKIT_INDEX_ALL_CONTENT}[query]`)], '')
  ));

  const handleClick: React.MouseEventHandler = (e) => {
    e.preventDefault();
    setVisible(!visible);
  };

  const checkIfVisibleByDefault = useCallback(() => {
    try {
      const urlParams = decodeURI(window.location.search);

      if (urlParams.indexOf('[refinementList]') > -1 || urlParams.indexOf('[menu]') > -1) {
        setVisible(true);
      }
    } catch (e) {
      console.warn('Could not parse query parameters to determine if filters should be opened');
    }
  }, []);

  useEffect(() => {
    dispatch(clearPageInformation());
    dispatch(setPageInformation(PAGE_TITLE));
    dispatch(setPageViewSent(false));
    checkIfVisibleByDefault();
  }, [dispatch]);

  const className = c('FilterButton Button', {
    'FilterButton--uncollapsed': visible,
  });

  const classNameInfo = c('Searchkit__Facets', {
    'Searchkit__Facets--visible': visible,
  });

  const goToUrl = useCallback((url: string) => {
    history.push(url);
  }, [history]);

  return (
    <>
      <Helmet
        htmlAttributes={{
          lang: DEFAULT_LANGUAGE,
        }}
      >
        <title>{PAGE_TITLE}</title>
      </Helmet>

      <SearchAllContent className={classNames}>
        {({ refine }) => (
          <>
            <div className="Searchkit__search-wrapper" id="main">
              <Container>
                <h1 className="visually-hidden">{t('general.search_title')}</h1>
                <div className="row">
                  <div className="col-12">
                    <SearchAllContent.Autocomplete
                      type="secondary"
                      onSearchSubmit={(searchQuery) => {
                        // Refine.
                        refine(searchQuery);

                        // Push the search query to GTM.
                        if (typeof window !== 'undefined') {
                          window.dataLayer = window.dataLayer || [];
                          window.dataLayer.push({
                            event: 'search',
                            searchTerm: searchQuery,
                          });
                        }
                      }}
                      onSuggestionSelected={(suggestion) => {
                        if (suggestion.autocomplete_text) {
                          refine(suggestion.autocomplete_text[0]);
                          return;
                        }

                        goToUrl(suggestion.url[0]);
                      }}
                      autocompleteProps={{
                        initialUiState: {
                          [SEARCHKIT_INDEX_ALL_CONTENT]: {
                            query: decodeURI(initialAutocompleteQuery),
                          },
                        },
                      }}
                    />

                    <div className="Searchkit__search__filters">
                      <div className="Searchkit__search__filters__topbar">
                        <button type="button" className={className} onClick={handleClick}>
                          Filters <span className={`icon ${visible ? 'icon-minus' : 'icon-plus'}`} />
                        </button>
                        <SearchStats />

                      </div>
                      <div className={classNameInfo}>
                        <SearchAllContent.Facets className={!visible ? 'closed' : 'open'} />
                      </div>
                    </div>
                  </div>
                </div>
              </Container>
            </div>

            <div className="Searchkit__results-wrapper">
              <Container>
                <div className="row">
                  <div className="col-12">
                    <SearchAllContent.Results />
                  </div>
                </div>
              </Container>
            </div>
          </>
        )}
      </SearchAllContent>
    </>
  );
};

export default SearchkitSearch;
